import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, Wallet, Loader2, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { EnvelopeWithProgress, CreateEnvelopeDto } from '../../types';
import api from '../../services/api';
import OptionToggle from '../common/OptionToggle';
import FloatingField from '../common/FloatingField';
import SegmentedControl from '../common/SegmentedControl';

interface EnvelopeConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  type: 'shared' | 'personal';
  editingEnvelope: EnvelopeWithProgress | null;
  bankAccounts?: { id: string; name: string; color: string }[];
  /** Bascule vers l'onglet Budget (création). Absent = pas d'onglets. */
  onSwitchType?: () => void;
}

// Nombre de mois entiers d'ici la date cible (au moins 1).
function monthsUntil(dateStr: string): number {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 6;
  const now = new Date();
  return Math.max(1, (d.getFullYear() - now.getFullYear()) * 12 + (d.getMonth() - now.getMonth()));
}

const defaultFormData: CreateEnvelopeDto = {
  name: '',
  targetAmount: 0,
  targetDate: '',
  bankAccountIds: null,
};

export default function EnvelopeConfigModal({
  isOpen,
  onClose,
  onSaveSuccess,
  type,
  editingEnvelope,
  bankAccounts: propBankAccounts,
  onSwitchType,
}: EnvelopeConfigModalProps) {
  const { t, i18n } = useTranslation('dashboard');
  const { t: tc } = useTranslation('common');
  const fmt = (n: number) =>
    new Intl.NumberFormat(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { maximumFractionDigits: 0 }).format(Math.round(n || 0));
  const [formData, setFormData] = useState<CreateEnvelopeDto>(defaultFormData);
  const [allAccountsSelected, setAllAccountsSelected] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<{ id: string; name: string; color: string }[]>(propBankAccounts || []);

  // Charger les comptes bancaires si non fournis
  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      if (!propBankAccounts?.length) {
        setLoadingData(true);
        try {
          const endpoint = type === 'personal' ? '/bank-accounts' : '/shared-bank-accounts';
          const response = await api.get(endpoint);
          if (response.data.success) {
            setBankAccounts(response.data.data.map((a: { id: string; name: string; color: string }) => ({
              id: a.id,
              name: a.name,
              color: a.color,
            })));
          }
        } catch (err) {
          console.error('Erreur chargement comptes:', err);
        } finally {
          setLoadingData(false);
        }
      }
    };
    loadData();
  }, [isOpen, type, propBankAccounts]);

  useEffect(() => {
    if (propBankAccounts?.length) setBankAccounts(propBankAccounts);
  }, [propBankAccounts]);

  // Charger les données de l'enveloppe en édition
  useEffect(() => {
    if (editingEnvelope) {
      setFormData({
        name: editingEnvelope.name,
        targetAmount: editingEnvelope.targetAmount,
        targetDate: editingEnvelope.targetDate.split('T')[0],
        bankAccountIds: editingEnvelope.bankAccountIds,
      });
      if (editingEnvelope.bankAccountIds && editingEnvelope.bankAccountIds.length > 0) {
        setAllAccountsSelected(false);
        setSelectedAccounts(editingEnvelope.bankAccountIds);
      } else {
        setAllAccountsSelected(true);
        setSelectedAccounts([]);
      }
    } else {
      setFormData(defaultFormData);
      setAllAccountsSelected(false);
      setSelectedAccounts([]);
    }
    setError(null);
  }, [editingEnvelope, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (bankAccounts.length > 0 && !allAccountsSelected && selectedAccounts.length === 0) {
      setError(t('envelopeConfig.saveError'));
      return;
    }

    setSaving(true);

    try {
      const payload: Partial<CreateEnvelopeDto> = {
        name: formData.name,
        targetAmount: formData.targetAmount,
        targetDate: formData.targetDate,
      };

      if (bankAccounts.length > 0) {
        if (allAccountsSelected) {
          payload.bankAccountIds = null;
        } else if (selectedAccounts.length > 0) {
          payload.bankAccountIds = selectedAccounts;
        }
      }

      if (editingEnvelope) {
        await api.put(`/envelopes/${editingEnvelope.id}`, payload);
      } else {
        await api.post(`/envelopes?type=${type}`, payload);
      }

      onSaveSuccess();
    } catch (err: unknown) {
      let errorMessage = t('envelopeConfig.saveError');
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; code?: string } } };
        if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        }
      }
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingEnvelope) return;
    if (!confirm(t('envelopeConfig.deleteEnvelope'))) return;

    setDeleting(true);
    try {
      await api.delete(`/envelopes/${editingEnvelope.id}`);
      onSaveSuccess();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('envelopeConfig.deleteError');
      setError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleAllAccountsToggle = () => {
    if (allAccountsSelected) {
      setAllAccountsSelected(false);
    } else {
      setAllAccountsSelected(true);
      setSelectedAccounts([]);
    }
  };

  const toggleAccount = (accountId: string) => {
    setAllAccountsSelected(false);
    setSelectedAccounts(prev =>
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const months = monthsUntil(formData.targetDate);

  return createPortal(
    <div className="prov-overlay">
      <div className="prov-backdrop" onClick={onClose} />
      <div className="prov-wrap">
        <div className="prov-card" role="dialog" aria-label={editingEnvelope ? t('envelopeConfig.editEnvelope') : t('envelopeConfig.newEnvelope')}>
          {/* En-tête */}
          <header className="prov-head">
            <span className="prov-icon"><Wallet className="w-[22px] h-[22px]" /></span>
            <div className="min-w-0">
              <h2 className="prov-title">{editingEnvelope ? t('envelopeConfig.editEnvelope') : t('envelopeConfig.newEnvelope')}</h2>
              <p className="prov-sub">{t('envelopeConfig.subtitle')}</p>
            </div>
            <button className="prov-close" onClick={onClose} aria-label={tc('close')}><X className="w-[18px] h-[18px]" /></button>
          </header>

          {/* Onglets Enveloppe / Budget (création uniquement) */}
          {!editingEnvelope && onSwitchType && (
            <div className="prov-tabs">
              <SegmentedControl
                block
                ariaLabel={t('provision.preview')}
                value="envelope"
                onChange={(v) => { if (v === 'budget') onSwitchType(); }}
                options={[
                  { value: 'envelope', label: t('provision.tabEnvelope') },
                  { value: 'budget', label: t('provision.tabBudget') },
                ]}
              />
            </div>
          )}

          <div className="prov-body">
            {/* Colonne formulaire */}
            <form onSubmit={handleSubmit} className="prov-form">
              <div className="prov-fields">
                {error && <div className="prov-error">{error}</div>}
                {loadingData ? (
                  <div className="prov-loading"><Loader2 className="w-6 h-6 animate-spin" /></div>
                ) : (
                  <>
                    <FloatingField
                      label={`${t('envelopeConfig.name')} *`}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      maxLength={100}
                      required
                    />
                    <FloatingField
                      label={`${t('envelopeConfig.targetAmount')} *`}
                      type="text"
                      inputMode="decimal"
                      suffix="€"
                      className="num"
                      value={formData.targetAmount || ''}
                      onChange={(e) => setFormData({ ...formData, targetAmount: parseFloat(e.target.value.replace(',', '.')) || 0 })}
                      required
                    />
                    <FloatingField
                      label={`${t('envelopeConfig.targetDate')} *`}
                      type="date"
                      value={formData.targetDate}
                      onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                      required
                    />
                    {bankAccounts && bankAccounts.length > 0 && (
                      <div className="field-group">
                        <span className="field-label">{t('envelopeConfig.bankAccounts')}</span>
                        <div className="flex flex-col gap-2">
                          <OptionToggle
                            selected={allAccountsSelected}
                            onToggle={handleAllAccountsToggle}
                            icon={<Layers className="w-[18px] h-[18px]" />}
                          >
                            <span className="font-semibold">{t('envelopeConfig.allAccounts')}</span>
                          </OptionToggle>
                          {bankAccounts.map(account => (
                            <OptionToggle
                              key={account.id}
                              selected={selectedAccounts.includes(account.id)}
                              onToggle={() => toggleAccount(account.id)}
                              color={account.color}
                              icon={<Wallet className="w-[18px] h-[18px]" />}
                            >
                              {account.name}
                            </OptionToggle>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="prov-foot">
                {editingEnvelope && (
                  <button type="button" onClick={handleDelete} disabled={deleting} className="btn btn-danger">
                    <Trash2 className="w-4 h-4" />
                    {deleting ? t('envelopeConfig.deleting') : tc('delete')}
                  </button>
                )}
                <div className="foot-spacer" />
                <button type="button" onClick={onClose} className="btn btn-ghost">{tc('cancel')}</button>
                <button
                  type="submit"
                  disabled={saving || !formData.name || !formData.targetAmount || !formData.targetDate}
                  className="btn btn-primary"
                >
                  {saving ? t('envelopeConfig.saving') : (editingEnvelope ? tc('edit') : t('envelopeConfig.create'))}
                </button>
              </div>
            </form>

            {/* Colonne aperçu vivant */}
            <aside className="prov-preview">
              <span className="eyebrow">{t('provision.preview')}</span>
              <div className="prov-prev-card">
                <div className="prov-prev-top">
                  <div className="min-w-0">
                    <div className="prov-prev-name">{formData.name || t('envelopeConfig.namePlaceholder')}</div>
                    <div className="prov-prev-sub">{editingEnvelope ? t('envelopeConfig.editEnvelope') : t('envelopeConfig.newEnvelope')}</div>
                  </div>
                  <span className="prov-state">{t('envelopeConfig.stateSaving')}</span>
                </div>
                <div>
                  <div className="prov-prev-fig"><b className="num">{fmt(formData.targetAmount)} €</b><span>{t('envelopeConfig.toSetAside')}</span></div>
                  <div className="prov-bar"><span style={{ width: '0%' }} /></div>
                </div>
                <div className="prov-prev-foot">
                  <span>{t('envelopeConfig.monthlyEffort')} <b className="num">≈ {fmt(formData.targetAmount / months)} €</b></span>
                  <span>{t('envelopeConfig.overMonths', { count: months })}</span>
                </div>
              </div>
              <div className="prov-hint">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><circle cx="12" cy="12" r="9" /><path d="M12 8.2v.2M12 11.4v4.4" strokeLinecap="round" /></svg>
                <div>
                  <div className="prov-cycle">
                    <span className="dot" style={{ background: 'var(--pa)' }} /><span className="now">{t('envelopeConfig.cycleSaving')}</span>
                    <span>→</span><span className="dot" style={{ background: 'var(--debit)' }} /><span>{t('envelopeConfig.cycleSpending')}</span>
                    <span>→</span><span className="dot" style={{ background: 'var(--credit)' }} /><span>{t('envelopeConfig.cycleDone')}</span>
                  </div>
                  {t('envelopeConfig.cycleText')}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
